import { EventStreamContentType, fetchEventSource } from '@microsoft/fetch-event-source'
import { useEffect } from 'react'
import { create } from 'zustand'

import { API_BASE_URL } from '@/config'
import { queryClient } from '@/config/query-client'
import { isServerUnavailable, refreshSession } from '@/services/api/client'
import { useAuthStore } from '@/stores/auth.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import { logger } from '@/utils/logger'
import { isNativeApp } from '@/utils/platform'

/**
 * Server-sent events from `GET /realtime/stream`.
 *
 * `EventSource` can't send a Bearer header, so the stream is read with fetch-event-source. One
 * connection per tab, opened by {@link useRealtime} in the app shell; features listen with
 * {@link subscribeRealtime}. The stream closes while the tab is hidden and reconnects (with
 * backoff, refreshing the session on 401) when it comes back; after a reconnect active-call queries
 * are refetched because events sent in between are lost.
 *
 * In the Android app native HTTP buffers responses, which breaks SSE: the stream stays off there
 * and the call dock polls instead.
 */

type Listener = (data: unknown) => void

const listeners = new Map<string, Set<Listener>>()

/** Calls `listener` with the parsed payload of every `eventType` event. Returns the unsubscribe function. */
export function subscribeRealtime(eventType: string, listener: Listener): () => void {
  const set = listeners.get(eventType) ?? new Set<Listener>()
  set.add(listener)
  listeners.set(eventType, set)
  return () => {
    set.delete(listener)
  }
}

function dispatch(eventType: string, raw: string) {
  const set = listeners.get(eventType)
  if (!set?.size) return
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    logger.warn(`Realtime: ignoring malformed ${eventType} event`)
    return
  }
  set.forEach((listener) => listener(data))
}

interface RealtimeState {
  /** The stream is open; when false, screens that need live data poll instead. */
  connected: boolean
  setConnected: (connected: boolean) => void
}

export const useRealtimeStore = create<RealtimeState>((set) => ({
  connected: false,
  setConnected: (connected) => set({ connected }),
}))

class StreamClosed extends Error {}
class SessionExpired extends Error {}
class StreamRejected extends Error {}

const MAX_BACKOFF_MS = 30_000

function backoff(attempt: number): number {
  const base = Math.min(MAX_BACKOFF_MS, 1_000 * 2 ** Math.min(attempt, 5))
  return base / 2 + Math.random() * (base / 2)
}

function sleep(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms)
    signal.addEventListener('abort', () => {
      clearTimeout(timer)
      resolve()
    }, { once: true })
  })
}

/** Opens the stream for the signed-in user; mount once (AppShell). */
export function useRealtime() {
  const accessToken = useAuthStore((s) => s.accessToken)
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId)

  useEffect(() => {
    if (!accessToken || isNativeApp()) return

    const controller = new AbortController()
    const { setConnected } = useRealtimeStore.getState()

    const run = async () => {
      let attempt = 0
      let everConnected = false
      while (!controller.signal.aborted) {
        try {
          // Headers are fixed per connection, so each attempt reads the current token.
          await fetchEventSource(`${API_BASE_URL}/realtime/stream`, {
            signal: controller.signal,
            headers: { Authorization: `Bearer ${useAuthStore.getState().accessToken ?? ''}` },
            openWhenHidden: false,
            async onopen(response) {
              const contentType = response.headers.get('content-type') ?? ''
              if (response.ok && contentType.startsWith(EventStreamContentType)) {
                attempt = 0
                setConnected(true)
                if (everConnected) {
                  // Events sent while we were away are gone: refetch what they would have updated.
                  void queryClient.invalidateQueries({ queryKey: ['workspace', useWorkspaceStore.getState().currentWorkspaceId, 'call'] })
                }
                everConnected = true
                return
              }
              if (response.status === 401) throw new SessionExpired()
              throw new StreamRejected(`HTTP ${response.status}`)
            },
            onmessage(message) {
              if (!message.event || message.event === 'ready') return
              dispatch(message.event, message.data)
            },
            onclose() {
              // Server timeout or deploy: reconnect.
              throw new StreamClosed()
            },
            onerror(error) {
              // Never let the library retry with stale headers; the loop below reconnects.
              throw error
            },
          })
        } catch (error) {
          setConnected(false)
          if (controller.signal.aborted) return
          if (error instanceof SessionExpired) {
            try {
              await refreshSession()
            } catch (refreshError) {
              // Keep retrying: if the session is really over, the next API call signs the user out and
              // the cleared token aborts this loop.
              logger.debug('Realtime: session refresh failed', isServerUnavailable(refreshError), refreshError)
            }
          } else if (!(error instanceof StreamClosed)) {
            logger.debug('Realtime stream error, reconnecting', error)
          }
          await sleep(backoff(attempt++), controller.signal)
        }
      }
    }

    void run()
    return () => {
      controller.abort()
      setConnected(false)
    }
  }, [accessToken, workspaceId])
}
